/**
 * @auger/core — the Auger domain: records, fields, links, and the rules for
 * reading and writing them. An interface supplies `query`/`mutate`; the domain
 * owns field semantics, enrichment, the filter DSL, and the write planner.
 *
 * The root is the domain itself. Its other entries: `@auger/core/gql` (read
 * queries), `@auger/core/gql/types` (shared GraphQL types), `@auger/core/fields`,
 * `@auger/core/operators` (the filter DSL) and `@auger/core/collections`.
 */

import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import { createCalendarService } from "./calendar/index.js"
import { createMailService } from "./mail/index.js"
import type { User } from "./registries/users.js"
import { createCatalog } from "./services/catalog.js"
import {
  createAssetsService,
  createChangesService,
  createFollowsService,
  createGoogleService,
  createHistoryService,
  createListHandle,
  createListsService,
  createLocksService,
  createRecordEntryReads,
  createRecordHandle,
  createRecordsService,
  createRowsService,
  createSchemasService,
  createSharesService,
  createSourcesService,
  createTagsService,
  createTypesService,
  withLockErrors,
} from "./services/index.js"

/** Run a typed read (or any operation) and return its data. */
export type Query = <D, V>(op: TypedDocumentNode<D, V>, vars: V) => Promise<D>
/**
 * Options for one write. `role: "admin"` runs it as the Hasura role
 * heavybit_admin: the frontend sends asAdmin()'s header, the MCP sends
 * x-hasura-role. Every staff member's default role is heavybit_staff, so a
 * write that only admins may make (the data model, deleting tags and assets)
 * asks for the admin role here, per call. The caller must hold heavybit_admin,
 * or Hasura refuses the request.
 */
export interface MutateOptions {
  role?: "admin"
}
/** Run a typed write and return its data. */
export type Mutate = <D, V>(op: TypedDocumentNode<D, V>, vars: V, opts?: MutateOptions) => Promise<D>
export { AS_ADMIN } from "./asAdmin.js"
/** Build the domain over an interface's `query`/`mutate`. `record(id)` is the
 *  ergonomic per-record handle; the flat services are the batch/power surface.
 *
 *  `users` is a host's own user lookup (see `createCatalog`); `catalogTimeoutMs`
 *  fails a catalog request that hangs. */
export function createAuger(hostDeps: {
  query: Query
  mutate: Mutate
  users?: (id: number) => User | null
  catalogTimeoutMs?: number
}) {
  // One wrapper for every service: a lock guard's refusal, from any guarded
  // table, rethrows as its typed error.
  const deps = { ...hostDeps, mutate: withLockErrors(hostDeps.mutate) }
  const catalog = createCatalog({
    query: deps.query,
    users: deps.users,
    timeoutMs: deps.catalogTimeoutMs,
  })
  // A record's lists (entries / listings / matchings) sit beside its other reads.
  const records = { ...createRecordsService(deps), ...createRecordEntryReads(deps) }
  const sources = createSourcesService(deps)
  const types = createTypesService({ mutate: deps.mutate, catalog })
  const schemas = createSchemasService({ ...deps, catalog })
  // A list's rows: reads, row edits and the listing writes.
  const lists = createListsService({ ...deps, records })
  const rows = createRowsService(deps)
  const assets = createAssetsService(deps)
  const tags = createTagsService(deps)
  const shares = createSharesService(deps)
  // The viewer's explicit follows: who gets a DM when a note is added.
  const follows = createFollowsService(deps)
  const google = createGoogleService(deps)
  // The audit log: a record's changes, from either side of a join.
  const history = createHistoryService(deps)
  // Undo a changeset, and how much it touched.
  const changes = createChangesService(deps)
  const calendar = createCalendarService({ records, sources })
  const mail = createMailService({ records, sources, query: deps.query })

  return {
    records,
    sources,
    schemas,
    types,
    lists,
    rows,
    assets,
    tags,
    shares,
    follows,
    google,
    history,
    changes,
    calendar,
    mail,
    /** Lock or unlock a record; find the locked lists and records that refer to it,
     *  and the locked lists that use a schema. */
    locks: createLocksService({ query: deps.query, records }),
    catalog,
    /**
     * Load the catalog — schemas, types and users — and wire the registries the
     * field layer resolves through. Call once before any read or write.
     *
     * All three are required, and a host that loads some and forgets the rest
     * gets a failure that looks nothing like its cause: `enrichRecord` resolves
     * a record's fields from its own `schema_id` if present and from its type's
     * default schema otherwise, so a missing type registry leaves every
     * schema-less record field-less; an unwired users registry has every
     * user-typed field silently reading null. One call so none can be forgotten.
     *
     * It is `catalog.load()`: one request, registered only once it has all
     * arrived, so a failed load never leaves a half-populated registry.
     */
    load: (): Promise<void> => catalog.load(),
    record: (id: number) => createRecordHandle(records, sources, deps.query, id),
    /** The one write path for a record in a list: an entry's listing and record
     *  together (see `createListHandle`). */
    list: (id: number) => createListHandle(id, { query: deps.query, lists, records }),
  }
}

export type Auger = ReturnType<typeof createAuger>

export {
  CATALOG_MAX_AGE_MS,
  type Catalog,
  type CatalogService,
  type CatalogType,
  type CatalogStatus,
} from "./services/catalog.js"

// Reference registries — the catalog wires them on load.
export {
  registerSchemas,
  getSchema,
  type Schema,
  type SchemaField,
  type SchemaLike,
  type SchemaLayout,
} from "./registries/schemas.js"
export {
  registerTypes,
  getType,
  type RecordType,
  type RecordTypeSchema,
} from "./registries/types.js"
export { registerUsers, getUserById, type User } from "./registries/users.js"
export {
  buildDirectory,
  type DirectoryPersonRow,
  HEAVYBIT_ORG_RECORD_ID,
  type DirectoryRows,
  PARTNERS_LIST_SLUG,
  STAFF_LIST_SLUG,
} from "./directory.js"

export {
  EntryListMismatchError,
  ListFillError,
  RelationFieldInBatchError,
  SchemaInUseError,
  SourceConflictError,
  isLockError,
  lockError,
  RecordLockAgentError,
  RecordLockCombinedError,
  MAX_LOCK_REASON,
  RecordLockedError,
  SchemaLockedError,
  type LockedReferrer,
  type LockError,
  type SchemaLockedList,
} from "./services/index.js"
export { historyWhere } from "./services/index.js"
export {
  assetWhere,
  assetFolder,
  matchAssets,
  MEDIA_MATCHERS,
  extractTags,
} from "./services/index.js"

// Calendar triage: the rules, and the shapes the `calendar` service takes.
export {
  CATEGORIES,
  CATEGORY_SCHEMAS,
  CATEGORY_RESOURCE_TYPES,
  PIPEDRIVE_SOURCE_TYPES,
  categoryForSchema,
  createCalendarService,
  eventCategory,
  isDeclined,
  needsTriage,
  pipedriveSourceId,
  pipedriveTypeFor,
  resourceLinkKey,
  resourceTypeFor,
  schemasForCategory,
  type CalendarEvent,
  type CategorySchema,
  type CreateEventInput,
  type CreatedEvent,
  type MirrorSubject,
  type Mirrored,
  type PipedriveCreates,
} from "./calendar/index.js"

// Source types: the external systems a record's sources come from.
export * from "./sources/index.js"

// Mail: Gmail threads saved as Email Thread documents, and their links.
export type {
  DealMatch,
  LinkedThread,
  LinkThreadInput,
  SaveMessage,
  SavedThread,
  SaveThreadInput,
  ThreadLink,
} from "./mail/index.js"

// Event programming: guest funnel, readiness, series editions.
export * from "./events/index.js"

// Service input types + the record-handle relation-target shapes.
export type {
  CreateRecordInput,
  RelationApi,
  UpdateResult,
  Relation,
  WrittenRows,
  WrittenRowTypes,
  LinkTarget,
  ContactTarget,
  TimeTarget,
  ListingItem,
  FilteredEntries,
  ListCounts,
  AddListingInput,
  ListingChanges,
  ArchiveManyResult,
  UnarchiveManyResult,
  CreatedRecord,
  FullRecord,
  ShareArchiveState,
  ShareRow,
  ListedSource,
  SourceRow,
  SchemaRow,
  TypeRow,
  ListRecord,
  PatchableRelationship,
  PatchedRowOf,
  PrimaryRelationship,
  ListedRecord,
  RecordRow,
  EmailMatch,
  NameMatch,
  SearchedRecord,
  CreateSchemaInput,
  UpdateSchemaPatch,
  SchemaUsage,
  CreateTypeInput,
  UpdateTypePatch,
  AssetItem,
  CreateAssetInput,
  UpdateAssetPatch,
  MediaDimensions,
  UploadAssetInput,
  UploadTicket,
  RemoveAssetsResult,
  AssetView,
  AssetSort,
  AssetSearch,
  AssetSearchItem,
  AssetSearchResult,
  AssetCounts,
  AssetAttachment,
  MediaMatcher,
  MediaMatch,
  TagItem,
  TagWithParentItem,
  CreateTagInput,
  UpdateTagPatch,
  RemoveTagsResult,
  Relationship,
  RowChanges,
  RemoveRowsResult,
  UpdateRowsResult,
  SetPrimaryResult,
  HistoryCounts,
  HistoryFilters,
  HistoryPage,
  HistoryUser,
  OwnChange,
  RecordChange,
} from "./services/index.js"
